"use client";

import { useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Award, Download, ImagePlus, Mail, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { useAuth } from "@/components/providers/auth-provider";
import { ApiError } from "@/lib/api";
import { cfImageUrl } from "@/lib/cloudflare-image";
import { uploadAvatar } from "@/features/profile/api";

import { downloadContestCertificate, downloadContestCertificateSample } from "../../api";
import { useContestCertificates, useEmailContestCertificates, useSaveContestCertificates } from "../../hooks";
import { formatDateTime } from "../../lib/format";
import { useContestDisplayZone } from "../../lib/display-zone";
import type {
  ContestCertificateEmailState,
  ContestCertificateRecipient,
  ContestCertificateSettingsInput,
  ContestCertificatesView,
  OrganizerContestDetail,
} from "../../types";
import { ErrorNote, SuccessNote, messageOf } from "./ui";

/**
 * "Certificates with your logo" (`EX-DAY06`, Wave 20 decision 1, `docs/29`
 * §6.12): on a contest an Event Pack funds, the owner sets who presents the
 * certificates, the logo, up to two signatures and the lines, until the first
 * certificate is issued. Once the board is final, everybody who runs the
 * contest can download any contestant's certificate here: for printing, or
 * for somebody whose issued login has expired.
 *
 * Images go through `POST /v1/images`, the team logo's route, and are kept as
 * ids. What the server fixes (the lock, the lines' rules, who is owed one) is
 * read, never re-derived.
 *
 * `EX-CRT01`: the server emails every certificate on its own once the board
 * is final; the list says how each email went, "Email the certificates"
 * sends whatever was never sent, and a row's Email sends one again. When some
 * were already emailed, the press asks first (the founder, 2026-10-25): how
 * many went and when, and whether to send only the rest or everyone again.
 */

/** The refusals this card words itself; anything else prints the server's own sentence. */
const KNOWN_REASONS = [
  "certificates_issued",
  "certificate_image_missing",
  "no_event_pack",
  "certificate_images_unavailable",
  "board_not_final",
  "still_judging",
  "recently_emailed",
  "no_account",
  "temp_account",
  "no_email",
] as const;
type KnownReason = (typeof KNOWN_REASONS)[number];

/** The message key for a refusal, or `null` for one the server words better. */
export function certificateErrorKey(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.details || typeof err.details !== "object") return null;
  const reason = (err.details as { reason?: unknown }).reason;
  return KNOWN_REASONS.includes(reason as KnownReason) ? `errors.${String(reason)}` : null;
}

/** What stops a save before it is sent, or `null`. The server checks the same and more. */
export function certificateDraftProblem(
  draft: ContestCertificateSettingsInput,
): "needIssuer" | "needLine" | "needSignature" | null {
  if (draft.issuerName.trim().length < 2) return "needIssuer";
  if (draft.enabled && !draft.participation && draft.rankTopN === null) return "needLine";
  if (draft.signatories.some((s) => !s.imageId || !s.name.trim() || !s.title.trim())) return "needSignature";
  return null;
}

/** `EX-CRT01`: the message key of a certificate's email state, or `null` before it is claimed. */
export function certificateEmailKey(email: ContestCertificateEmailState | null | undefined): string | null {
  if (!email) return null;
  if (email.outcome === "skipped" && email.reason) return `email_skipped_${email.reason}`;
  return `email_${email.outcome}`;
}

/** `EX-CRT01`: how the list's emails stand, for the "already emailed" question. */
export interface CertificateEmailCounts {
  total: number;
  /** Emailed, or on its way. */
  emailed: number;
  /** Not issued yet, or never claimed: the plain press sends these. */
  notYet: number;
  failed: number;
  /** Skipped: a roster name, an issued login or an account with no address. */
  nobody: number;
  /** The latest send, as the server stamped it, or `null`. */
  lastSentAt: string | null;
}

export function certificateEmailCounts(recipients: readonly ContestCertificateRecipient[]): CertificateEmailCounts {
  const counts: CertificateEmailCounts = { total: recipients.length, emailed: 0, notYet: 0, failed: 0, nobody: 0, lastSentAt: null };
  for (const recipient of recipients) {
    const email = recipient.certificate?.email;
    if (!email) counts.notYet += 1;
    else if (email.outcome === "failed") counts.failed += 1;
    else if (email.outcome === "skipped") counts.nobody += 1;
    else {
      counts.emailed += 1;
      if (email.outcome === "sent" && (!counts.lastSentAt || email.at > counts.lastSentAt)) counts.lastSentAt = email.at;
    }
  }
  return counts;
}

/** The organizer's list, narrowed by a name, a team or an institution. */
export const CERTIFICATE_LIST_LIMIT = 50;
export function filterCertificateRecipients(
  recipients: readonly ContestCertificateRecipient[],
  query: string,
): ContestCertificateRecipient[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...recipients];
  return recipients.filter((r) =>
    [r.name, r.teamName ?? "", r.institution ?? ""].some((value) => value.toLowerCase().includes(needle)),
  );
}

function initialDraft(view: ContestCertificatesView): ContestCertificateSettingsInput {
  const saved = view.settings;
  if (saved) {
    return {
      enabled: saved.enabled,
      issuerName: saved.issuerName,
      logoImageId: saved.logoImageId,
      signatories: saved.signatories.map((s) => ({ ...s })),
      participation: saved.participation,
      rankTopN: saved.rankTopN,
    };
  }
  return {
    enabled: true,
    // `EX-OFF01`: a Progsity round starts from "Progsity"; everyone else names their own.
    // `EX-HST03`: a host organization starts it, and its logo, from the organization.
    issuerName: view.issuerDefault ?? "",
    logoImageId: view.logoDefault ?? null,
    signatories: [],
    participation: true,
    rankTopN: view.bounds.rankTopNDefault,
  };
}

function stateOf(view: ContestCertificatesView): { key: "off" | "on" | "fixed"; tone: StatusTone } {
  if (!view.settings?.enabled) return { key: "off", tone: "neutral" };
  return view.lockedAt ? { key: "fixed", tone: "info" } : { key: "on", tone: "success" };
}

export function CertificatesCard({ contest, locale }: { contest: OrganizerContestDetail; locale: string }) {
  const view = contest.certificates;
  if (!view) return null;
  return <CertificatesCardBody contest={contest} view={view} locale={locale} />;
}

function CertificatesCardBody({
  contest,
  view,
  locale,
}: {
  contest: OrganizerContestDetail;
  view: ContestCertificatesView;
  locale: string;
}) {
  const t = useTranslations("contest.organizer.console.overview.certificates");
  const zone = useContestDisplayZone();
  const { getToken } = useAuth();
  const save = useSaveContestCertificates(contest._id);
  const [draft, setDraft] = useState<ContestCertificateSettingsInput>(() => initialDraft(view));
  const [uploading, setUploading] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const signatureInputs = useRef<(HTMLInputElement | null)[]>([]);

  const canEdit = contest.viewerScope.canManageSettings && !view.lockedAt;
  const state = stateOf(view);
  const settings = view.settings;

  const upload = async (slot: string, file: File | undefined, apply: (id: string) => void) => {
    const token = getToken();
    if (!file || !token) return;
    setUploading(slot);
    setError(null);
    try {
      const uploaded = await uploadAvatar(file, token);
      apply(uploaded.id);
    } catch (err) {
      setError(messageOf(err, t("uploadFailed")));
    } finally {
      setUploading(null);
    }
  };

  const setSignatory = (index: number, patch: Partial<ContestCertificateSettingsInput["signatories"][number]>) =>
    setDraft((current) => ({
      ...current,
      signatories: current.signatories.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));

  const submit = async () => {
    setError(null);
    setSaved(null);
    const problem = certificateDraftProblem(draft);
    if (problem) {
      setError(t(problem));
      return;
    }
    try {
      await save.mutateAsync({
        ...draft,
        issuerName: draft.issuerName.trim(),
        signatories: draft.signatories.map((s) => ({ ...s, name: s.name.trim(), title: s.title.trim() })),
      });
      setSaved(t("saved"));
    } catch (err) {
      const key = certificateErrorKey(err);
      setError(key ? t(key) : messageOf(err, t("failed")));
    }
  };

  const preview = async () => {
    setError(null);
    setPreviewing(true);
    try {
      await downloadContestCertificateSample(contest._id);
    } catch (err) {
      const key = certificateErrorKey(err);
      setError(key ? t(key) : messageOf(err, t("previewFailed")));
    } finally {
      setPreviewing(false);
    }
  };

  const busy = save.isPending || uploading !== null;
  const logoUrl = draft.logoImageId ? cfImageUrl(draft.logoImageId, "card") : "";

  return (
    <Card aria-labelledby="certificates-title" data-testid="certificates-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle id="certificates-title" className="flex items-center gap-2 text-sm font-bold text-neutral-900">
            <Award className="h-4 w-4 text-primary-ink" aria-hidden />
            {t("title")}
          </CardTitle>
          <StatusBadge tone={state.tone} label={t(`state_${state.key}`)} />
        </div>
        <p className="mt-2 text-xs leading-relaxed text-[rgb(var(--muted-foreground))]">{t("lead")}</p>
      </CardHeader>
      <CardContent className="space-y-5">
        {canEdit ? (
          <div className="space-y-5" data-testid="certificates-form">
            <label className="flex items-start gap-2 text-sm font-semibold text-neutral-800">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                disabled={busy}
              />
              {t("enabled")}
            </label>

            <div className="max-w-xl">
              <label htmlFor="certificates-issuer" className="block text-xs font-semibold text-neutral-700">
                {t("issuerLabel")}
              </label>
              <Input
                id="certificates-issuer"
                className="mt-1"
                value={draft.issuerName}
                maxLength={view.bounds.issuerMax}
                onChange={(event) => setDraft((current) => ({ ...current, issuerName: event.target.value }))}
                disabled={busy}
              />
              <p className="mt-1 text-[11px] text-[rgb(var(--muted-foreground))]">{t("issuerHint")}</p>
            </div>

            <div>
              <p className="text-xs font-semibold text-neutral-700">{t("logoLabel")}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {logoUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element -- a Cloudflare Images delivery URL, already sized
                  <img src={logoUrl} alt={t("logoAlt")} className="h-14 max-w-[180px] rounded-md border border-neutral-200 bg-white object-contain p-1" />
                ) : null}
                <input
                  ref={logoInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    void upload("logo", file, (id) => setDraft((current) => ({ ...current, logoImageId: id })));
                  }}
                />
                <Button variant="outline" onClick={() => logoInput.current?.click()} disabled={busy}>
                  <ImagePlus className="h-3.5 w-3.5" aria-hidden />
                  {draft.logoImageId ? t("replace") : t("uploadLogo")}
                </Button>
                {draft.logoImageId ? (
                  <Button
                    variant="tertiary"
                   
                    onClick={() => setDraft((current) => ({ ...current, logoImageId: null }))}
                    disabled={busy}
                  >
                    {t("remove")}
                  </Button>
                ) : null}
              </div>
              <p className="mt-1 text-[11px] text-[rgb(var(--muted-foreground))]">{t("logoHint")}</p>
            </div>

            <div>
              <p className="text-xs font-semibold text-neutral-700">{t("signaturesLabel")}</p>
              <p className="mt-0.5 text-[11px] text-[rgb(var(--muted-foreground))]">{t("signaturesHint")}</p>
              <div className="mt-2 grid gap-3 md:grid-cols-2">
                {draft.signatories.map((signatory, index) => {
                  const url = signatory.imageId ? cfImageUrl(signatory.imageId, "card") : "";
                  return (
                    <div key={index} className="rounded-[10px] border border-neutral-200 p-3" data-testid="certificates-signatory">
                      <div className="flex items-center justify-between gap-2">
                        {url ? (
                          // eslint-disable-next-line @next/next/no-img-element -- a Cloudflare Images delivery URL, already sized
                          <img
                            src={url}
                            alt={t("signatureAlt", { number: index + 1 })}
                            className="h-12 max-w-[160px] rounded-md border border-neutral-200 bg-white object-contain p-1"
                          />
                        ) : (
                          <span className="text-[11px] text-[rgb(var(--muted-foreground))]">{t("signatureAlt", { number: index + 1 })}</span>
                        )}
                        <Button
                          variant="tertiary"
                         
                          aria-label={t("removeSignature")}
                          onClick={() =>
                            setDraft((current) => ({ ...current, signatories: current.signatories.filter((_, i) => i !== index) }))
                          }
                          disabled={busy}
                        >
                          <X className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      </div>
                      <input
                        ref={(node) => {
                          signatureInputs.current[index] = node;
                        }}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = "";
                          void upload(`signature_${index}`, file, (id) => setSignatory(index, { imageId: id }));
                        }}
                      />
                      <Button
                        variant="outline"
                       
                        className="mt-2"
                        onClick={() => signatureInputs.current[index]?.click()}
                        disabled={busy}
                      >
                        <ImagePlus className="h-3.5 w-3.5" aria-hidden />
                        {signatory.imageId ? t("replace") : t("uploadSignature")}
                      </Button>
                      <label htmlFor={`certificates-signatory-name-${index}`} className="mt-3 block text-xs font-semibold text-neutral-700">
                        {t("signatureName")}
                      </label>
                      <Input
                        id={`certificates-signatory-name-${index}`}
                        className="mt-1"
                        value={signatory.name}
                        maxLength={view.bounds.signatoryNameMax}
                        onChange={(event) => setSignatory(index, { name: event.target.value })}
                        disabled={busy}
                      />
                      <label htmlFor={`certificates-signatory-title-${index}`} className="mt-2 block text-xs font-semibold text-neutral-700">
                        {t("signatureTitle")}
                      </label>
                      <Input
                        id={`certificates-signatory-title-${index}`}
                        className="mt-1"
                        value={signatory.title}
                        maxLength={view.bounds.signatoryTitleMax}
                        onChange={(event) => setSignatory(index, { title: event.target.value })}
                        disabled={busy}
                      />
                    </div>
                  );
                })}
              </div>
              {draft.signatories.length < view.bounds.signatoriesMax ? (
                <Button
                  variant="outline"
                 
                  className="mt-3"
                  onClick={() =>
                    setDraft((current) => ({ ...current, signatories: [...current.signatories, { name: "", title: "", imageId: "" }] }))
                  }
                  disabled={busy}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  {t("addSignature")}
                </Button>
              ) : null}
            </div>

            <fieldset className="space-y-3">
              <legend className="text-xs font-semibold text-neutral-700">{t("linesLabel")}</legend>
              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  checked={draft.participation}
                  onChange={(event) => setDraft((current) => ({ ...current, participation: event.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                  disabled={busy}
                />
                <span>
                  {t("participationLine")}
                  <span className="mt-0.5 block text-[11px] text-[rgb(var(--muted-foreground))]">{t("participationHint")}</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  checked={draft.rankTopN !== null}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      rankTopN: event.target.checked ? view.bounds.rankTopNDefault : null,
                    }))
                  }
                  className="mt-0.5 h-4 w-4 rounded border-neutral-300"
                  disabled={busy}
                />
                <span>
                  {t("rankLine")}
                  <span className="mt-0.5 block text-[11px] text-[rgb(var(--muted-foreground))]">{t("rankHint")}</span>
                </span>
              </label>
              {draft.rankTopN !== null ? (
                <div className="ml-6 max-w-[180px]">
                  <label htmlFor="certificates-rank-top-n" className="block text-xs font-semibold text-neutral-700">
                    {t("rankTopNLabel")}
                  </label>
                  <Input
                    id="certificates-rank-top-n"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={view.bounds.rankTopNMax}
                    className="mt-1"
                    value={draft.rankTopN}
                    onChange={(event) => {
                      const value = Math.round(Number(event.target.value));
                      const bounded = Number.isFinite(value) ? Math.min(view.bounds.rankTopNMax, Math.max(1, value)) : 1;
                      setDraft((current) => ({ ...current, rankTopN: bounded }));
                    }}
                    disabled={busy}
                  />
                </div>
              ) : null}
            </fieldset>
          </div>
        ) : (
          <div className="space-y-1.5 text-sm text-neutral-800" data-testid="certificates-summary">
            {settings ? (
              <>
                <p className="font-semibold text-neutral-900">{t("summaryIssuer", { name: settings.issuerName })}</p>
                {settings.participation ? <p>{t("summaryParticipation")}</p> : null}
                {settings.rankTopN !== null ? <p>{t("summaryRank", { count: settings.rankTopN })}</p> : null}
              </>
            ) : (
              <p className="text-[rgb(var(--muted-foreground))]">{contest.viewerScope.canManageSettings ? t("notSetUp") : t("ownerSets")}</p>
            )}
            {view.lockedAt ? (
              <p className="text-xs text-neutral-600" data-testid="certificates-fixed">
                {t("fixed", { when: formatDateTime(view.lockedAt, locale, zone), count: view.issuedCount })}
              </p>
            ) : null}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {canEdit ? (
            <Button onClick={() => void submit()} disabled={busy}>
              {t("save")}
            </Button>
          ) : null}
          {settings ? (
            <Button variant="outline" onClick={() => void preview()} disabled={previewing || busy}>
              {t("preview")}
            </Button>
          ) : null}
        </div>
        <ErrorNote message={error} />
        <SuccessNote message={saved} />

        {settings?.enabled ? (
          view.boardFinal ? (
            <CertificateDownloads contestId={contest._id} slug={contest.slug} locale={locale} />
          ) : (
            <p className="text-xs text-[rgb(var(--muted-foreground))]">{t("downloadsWaiting")}</p>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Once the board is final: any contestant's certificate, for the people who run the contest. */
function CertificateDownloads({ contestId, slug, locale }: { contestId: string; slug: string; locale: string }) {
  const t = useTranslations("contest.organizer.console.overview.certificates");
  const zone = useContestDisplayZone();
  const list = useContestCertificates(slug, true);
  const email = useEmailContestCertificates(contestId, slug);
  const [emailNote, setEmailNote] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [query, setQuery] = useState("");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recipients = useMemo(() => list.data?.recipients ?? [], [list.data]);
  const matches = useMemo(() => filterCertificateRecipients(recipients, query), [recipients, query]);
  const shown = matches.slice(0, CERTIFICATE_LIST_LIMIT);
  const counts = useMemo(() => certificateEmailCounts(recipients), [recipients]);

  const download = async (recipient: ContestCertificateRecipient) => {
    setError(null);
    setDownloading(recipient.recipientId);
    try {
      await downloadContestCertificate(slug, recipient.recipientId, `${recipient.name}.pdf`);
      void list.refetch();
    } catch (err) {
      const key = certificateErrorKey(err);
      setError(key ? t(key) : messageOf(err, t("failed")));
    } finally {
      setDownloading(null);
    }
  };

  const send = async (recipient: ContestCertificateRecipient | null, resend = false) => {
    setError(null);
    setEmailNote(null);
    setAsking(false);
    try {
      const result = await email.mutateAsync(recipient ? { recipientId: recipient.recipientId } : resend ? { resend: true } : {});
      setEmailNote(
        result.scope === "all"
          ? t("emailAllDone", {
              sent: result.sent,
              skipped: result.skipped,
              failed: result.failed,
              already: result.alreadyEmailed,
              recent: result.recentlyEmailed ?? 0,
            })
          : result.outcome === "sent"
            ? t("emailOneDone", { name: recipient?.name ?? "" })
            : t("email_failed"),
      );
    } catch (err) {
      const key = certificateErrorKey(err);
      setError(key ? t(key) : messageOf(err, t("failed")));
    }
  };

  return (
    <section aria-labelledby="certificates-downloads-title" className="border-t border-neutral-200 pt-4">
      <h3 id="certificates-downloads-title" className="text-sm font-bold text-neutral-900">
        {t("downloadsTitle")}
      </h3>
      {list.isLoading ? (
        <div className="mt-3 space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : list.data?.state === "still_judging" ? (
        <p className="mt-2 text-xs text-[rgb(var(--muted-foreground))]">{t("judging")}</p>
      ) : (
        <>
          <p className="mt-1 text-xs text-[rgb(var(--muted-foreground))]">{t("downloadsLead")}</p>
          {recipients.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="certificates-email-all">
              <Button
                variant="outline"
                // Some already went: ask first, rather than a press that sends nothing and says so out of sight.
                onClick={() => (counts.emailed + counts.failed > 0 ? setAsking(true) : void send(null))}
                disabled={email.isPending || downloading !== null}
              >
                <Mail className="h-3.5 w-3.5" aria-hidden />
                {t("emailAll")}
              </Button>
              <p className="text-[11px] text-[rgb(var(--muted-foreground))]">{t("emailAllHint")}</p>
            </div>
          ) : null}
          <div className="mt-2">
            <SuccessNote message={emailNote} />
          </div>
          <Dialog
            open={asking}
            onClose={() => setAsking(false)}
            title={t("emailAskTitle")}
            closeLabel={t("emailAskCancel")}
            dismissible={!email.isPending}
          >
            <div className="space-y-2 text-sm text-neutral-700" data-testid="certificates-email-ask">
              <p>
                {counts.lastSentAt
                  ? t("emailAskSent", {
                      count: counts.emailed,
                      total: counts.total,
                      when: formatDateTime(counts.lastSentAt, locale, zone),
                    })
                  : t("emailAskSentNoDate", { count: counts.emailed, total: counts.total })}
              </p>
              {counts.notYet > 0 ? <p>{t("emailAskNotYet", { count: counts.notYet })}</p> : null}
              {counts.failed > 0 ? <p>{t("emailAskFailed", { count: counts.failed })}</p> : null}
              {counts.nobody > 0 ? <p>{t("emailAskNobody", { count: counts.nobody })}</p> : null}
              <p className="text-xs text-[rgb(var(--muted-foreground))]">{t("emailAskCooldown")}</p>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setAsking(false)} disabled={email.isPending}>
                {t("emailAskCancel")}
              </Button>
              {counts.notYet > 0 ? (
                <Button variant="outline" onClick={() => void send(null)} disabled={email.isPending}>
                  {t("emailAskRest", { count: counts.notYet })}
                </Button>
              ) : null}
              <Button onClick={() => void send(null, true)} disabled={email.isPending}>
                <Mail className="h-3.5 w-3.5" aria-hidden />
                {t("emailAskAgain")}
              </Button>
            </div>
          </Dialog>
          {recipients.length > 0 ? (
            <Input
              type="search"
              aria-label={t("search")}
              placeholder={t("search")}
              className="mt-3 max-w-sm"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          ) : null}
          {recipients.length === 0 ? (
            <p className="mt-3 text-sm text-[rgb(var(--muted-foreground))]">{t("none")}</p>
          ) : shown.length === 0 ? (
            <p className="mt-3 text-sm text-[rgb(var(--muted-foreground))]">{t("noMatch")}</p>
          ) : (
            <ul className="mt-3 divide-y divide-neutral-100" data-testid="certificates-downloads">
              {shown.map((recipient) => (
                <li key={recipient.recipientId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900">{recipient.name}</p>
                    <p className="truncate text-xs text-[rgb(var(--muted-foreground))]">
                      {[recipient.placement ?? t("line_participation"), recipient.teamName, recipient.institution]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <EmailStateLine email={recipient.certificate?.email ?? null} locale={locale} zone={zone} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="tertiary"
                      onClick={() => void send(recipient)}
                      disabled={email.isPending || downloading !== null}
                      aria-label={t("emailOne", { name: recipient.name })}
                    >
                      <Mail className="h-3.5 w-3.5" aria-hidden />
                      {t("email")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => void download(recipient)}
                      disabled={downloading !== null}
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden />
                      {t("download")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {matches.length > shown.length ? (
            <p className="mt-2 text-xs text-[rgb(var(--muted-foreground))]">{t("more", { count: matches.length - shown.length })}</p>
          ) : null}
        </>
      )}
      <ErrorNote message={error} />
    </section>
  );
}

/** `EX-CRT01`: one row's email, in the card's words. */
function EmailStateLine({
  email,
  locale,
  zone,
}: {
  email: ContestCertificateEmailState | null;
  locale: string;
  zone: ReturnType<typeof useContestDisplayZone>;
}) {
  const t = useTranslations("contest.organizer.console.overview.certificates");
  const key = certificateEmailKey(email);
  if (!key || !email) return null;
  return (
    <p className="truncate text-[11px] text-[rgb(var(--muted-foreground))]" data-testid="certificate-email-state">
      {t(key, { when: formatDateTime(email.at, locale, zone) })}
    </p>
  );
}
